'use client';

import { createContext, useContext, useMemo } from 'react';

import { getOrCreateMap } from './labelsCache';

export type LabelType =
  | 'authors'
  | 'editors'
  | 'binders'
  | 'publicationPlaces'
  | 'selections'
  | 'languages'
  | 'authorCountries'
  | 'subUniverses'
  | 'reference_interne'
  | 'catalogue';

export type LabelsContextValue = {
  labels: Record<LabelType, Map<string, string>>;
  isAdmin: boolean;
  apiKey: string;
};

const LabelsContext = createContext<LabelsContextValue | undefined>(undefined);

export interface LabelsProviderProps {
  children: React.ReactNode;
  locale?: string;
  authors?: Record<string, string>;
  editors?: Record<string, string>;
  binders?: Record<string, string>;
  publicationPlaces?: Record<string, string>;
  selections?: Record<string, string>;
  languages?: Record<string, string>;
  authorCountries?: Record<string, string>;
  subUniverses?: Record<string, string>;
  reference_interne?: Record<string, string>;
  catalogue?: Record<string, string>;
  isAdmin?: boolean;
  apiKey: string;
}

export function LabelsProvider({
  children,
  locale = 'fr',
  authors = {},
  editors = {},
  binders = {},
  publicationPlaces = {},
  selections = {},
  languages = {},
  authorCountries = {},
  subUniverses = {},
  reference_interne = {},
  catalogue = {},
  isAdmin = false,
  apiKey,
}: LabelsProviderProps) {
  const authorsMap = getOrCreateMap(locale, 'authors', authors);
  const editorsMap = getOrCreateMap(locale, 'editors', editors);
  const bindersMap = getOrCreateMap(locale, 'binders', binders);
  const publicationPlacesMap = getOrCreateMap(
    locale,
    'publicationPlaces',
    publicationPlaces,
  );
  const selectionsMap = getOrCreateMap(locale, 'selections', selections);
  const languagesMap = getOrCreateMap(locale, 'languages', languages);
  const authorCountriesMap = getOrCreateMap(
    locale,
    'authorCountries',
    authorCountries,
  );
  const subUniversesMap = getOrCreateMap(locale, 'subUniverses', subUniverses);
  const referenceInterneMap = getOrCreateMap(
    locale,
    'reference_interne',
    reference_interne,
  );
  const catalogueMap = getOrCreateMap(locale, 'catalogue', catalogue);

  const value: LabelsContextValue = useMemo(
    () => ({
      labels: {
        authors: authorsMap,
        editors: editorsMap,
        binders: bindersMap,
        publicationPlaces: publicationPlacesMap,
        selections: selectionsMap,
        languages: languagesMap,
        authorCountries: authorCountriesMap,
        subUniverses: subUniversesMap,
        reference_interne: referenceInterneMap,
        catalogue: catalogueMap,
      },
      isAdmin,
      apiKey,
    }),
    [
      authorsMap,
      editorsMap,
      bindersMap,
      publicationPlacesMap,
      selectionsMap,
      languagesMap,
      authorCountriesMap,
      subUniversesMap,
      referenceInterneMap,
      catalogueMap,
      isAdmin,
      apiKey,
    ],
  );

  return (
    <LabelsContext.Provider value={value}>{children}</LabelsContext.Provider>
  );
}

export function useLabels(type: LabelType): Map<string, string> {
  const context = useContext(LabelsContext);
  if (context === undefined) {
    throw new Error('useLabels must be used within a LabelsProvider');
  }
  return context.labels[type];
}

export function useIsAdmin(): boolean {
  const context = useContext(LabelsContext);
  if (context === undefined) {
    throw new Error('useIsAdmin must be used within a LabelsProvider');
  }
  return context.isAdmin;
}

export function useApiKey(): string {
  const context = useContext(LabelsContext);
  if (context === undefined) {
    throw new Error('useApiKey must be used within a LabelsProvider');
  }
  return context.apiKey;
}

export const useAuthorsLabels = (): Map<string, string> => useLabels('authors');
export const useEditorsLabels = (): Map<string, string> => useLabels('editors');
export const useBindersLabels = (): Map<string, string> => useLabels('binders');
export const usePublicationPlacesLabels = (): Map<string, string> =>
  useLabels('publicationPlaces');
export const useSelectionsLabels = (): Map<string, string> =>
  useLabels('selections');
export const useLanguagesLabels = (): Map<string, string> =>
  useLabels('languages');
export const useAuthorCountriesLabels = (): Map<string, string> =>
  useLabels('authorCountries');
export const useSubUniversesLabels = (): Map<string, string> =>
  useLabels('subUniverses');
